import { ACTIVITY_LOG_EVENTS } from '@/constants/activityLog';
import { CourseLevelCodeEnum } from '@/constants/course/courseLevel';
import { LocalStorageKeys } from '@/constants/enums/LocalStorageEnum';
import { initialMemberInfo, RoleTypeEnum } from '@/constants/user';
import { env } from '@/env';
import type { PermHiddenFunctionContentDto } from '@/models/interface/function.dto';
import type { AyceContractResponse, RemainingTickets } from '@/models/interface/member';
import type { MemberInfoI } from '@/models/interface/user';
import FDMessage from '@/plugins/fdMessage';
import {
  getToken,
  getTokenPayload,
  loginState,
  parseJwtToken,
  removeToken,
  setLoginStateCookie,
  tokenExpire
} from '@/utils/token';
import { UtilCommon } from '@/utils/utilCommon';
import dayjs from 'dayjs';

export const useUserStore = defineStore(
  'user',
  () => {
    // Token 失效的錯誤訊息
    const forceLogoutMsg = '由於頁面閒置過久，系統已自動登出。';

    // HTTP code 401 的錯誤訊息
    const unAuthorized = '您重複登入，系統已自動登出。';

    /**
     * 學期制方案預設資料
     */
    const defaultAyceContractData = (): AyceContractResponse => ({
      ayce: {
        id: 0,
        type: 0,
        contractName: '',
        label: '',
        displayTime: '',
        realTime: ''
      },
      startDate: '',
      endDate: ''
    });

    // 活動日誌請求
    const { createActivityLogsEvt } = useActivityLogsApi();
    const { t } = useI18n();

    const userAccount = ref('');
    const userPassword = ref('');
    const userData = ref<MemberInfoI>(initialMemberInfo);
    const rememberAccountAndPassword = ref(true);
    const earlyReserveOriginalCount = ref(10);
    const hasSales = ref<boolean | null>(null);
    const isPayUser = ref<boolean | null>(null);
    const phoneExist = ref<boolean | null>(null);
    const isWithdrawCoupon = ref<boolean | null>(null);
    const remainingTickets = ref<RemainingTickets>({
      courseTickets: 0,
      liveStreamFreeTickets: 0,
      liveStreamPaidTickets: 0
    });

    // 2026/04 新加入業務專屬的定期制方案資訊
    const ayceContractInfo: Ref<AyceContractResponse> = ref(defaultAyceContractData());

    const token = ref<string>(getToken() || '');
    const isLogin = ref<boolean>(false);

    // 解析 JWT Token，取得會員帳號基本資訊 (以 token 為響應來源)
    const tokenPayload = computed(() => {
      if (!token.value) return getTokenPayload();
      try {
        return parseJwtToken(token.value);
      } catch {
        return getTokenPayload();
      }
    });

    // 是否為第一次登入 (打 log 的判斷，會員推廣活動用)
    const isFirstLogin = ref<boolean>(false);
    const needGetMemberInfo = ref<boolean>(true);
    const popupFlag = ref<boolean>(false);

    /**
     * 識別 B 端及 C 端會員的編號
     *
     * @remarks
     * - 411: C 端會員
     * - 其他: B 端會員
     */
    const customerId = ref<number | null>(null);

    // 是否為註冊後
    const isAfterSignUp = ref(false);

    // 是否為b2b會員
    const isB2bUser = computed<boolean>(
      () => !!userData.value && userData.value.customerId !== 411
    );

    /**
     * 平台到期日是否已過期
     *
     * @remarks
     * 當無會員資訊或無 endDate，或今天日期超過 endDate 時視為已過期
     */
    const isPlatformExpired = computed<boolean>(() => {
      const endDate = userData.value?.endDate;
      if (!endDate) return true;

      try {
        const today = dayjs().startOf('day');
        const targetEndDate = dayjs(endDate).startOf('day');
        return today.isAfter(targetEndDate);
      } catch {
        return true;
      }
    });

    /**
     * 是否為有效付費會員
     *
     * @remarks
     * 具備付費身分或平台到期日尚未過期
     */
    const isPayUserActive = computed<boolean>(
      () => Boolean(isPayUser.value) || !isPlatformExpired.value
    );

    // AI 語音助理 ID
    const aiTeacherAssistant = ref<string>('');
    const isAfterThirdPartVerify = ref<boolean>(false);

    // 課程篩選類型 target: 依學習目標, all: 不限目標
    const courseFilterType = ref<string>('target');

    // 新註冊送咖啡券廣告蓋板顯示控制
    const showCoffeeCouponByStore = ref<boolean>(true);

    const adId = ref<number>(0);

    // 推廣活動圖片
    const adImageSrc = ref<{ [key: string]: string }>({
      pcUrl: '',
      phoneUrl: ''
    });

    // 推廣廣告身分別 (for EVENT)
    const adRole = ref<string>('');

    const social_Notification = ref<boolean>(false);

    // 隱藏功能列表 (訂閱/旁聽)
    const functionAccess = ref<PermHiddenFunctionContentDto>([]);

    // 是否隱藏訂閱按鈕
    const hiddenSubscriptionBtn = computed<boolean>(
      () => !functionAccess.value?.includes('Subscription')
    );

    // 會員可使用的網站版本 (V1 或 V2)
    const userSiteVersion = computed<string>(() => tokenPayload.value.siteVersion || 'V1');

    // 是否為 V2 版本官網會員
    const isV2OfficialUser = computed<boolean>(() => userSiteVersion.value === 'V2');

    // 已登入狀態條件（Token 存在且未過期）
    const hasLoginCondition = computed<boolean>(
      () => (isLogin.value || loginState()) && !tokenExpire(token.value)
    );

    // 會員頭像
    const userAvatar = computed<string>(() => {
      const photo = userData.value.photo;
      return photo
        ? `${photo}${photo.includes('?') ? '&' : '?'}cacheBuster=${dayjs().valueOf()}`
        : '/images/default/defaultAvatar.jpg';
    });

    // 會員名稱
    const userNickName = computed<string>(() => userData.value.nickName || '會員名稱');

    // 會員等級
    const levelLabel = computed<string | undefined>(() => {
      const memberLevel = userData.value.level;
      return Object.keys(CourseLevelCodeEnum).find(
        (key) => CourseLevelCodeEnum[key as keyof typeof CourseLevelCodeEnum] === memberLevel
      );
    });

    // 使用者資訊是否存在
    const hasUserInfo = computed<boolean>(() =>
      Boolean(userData.value.memberId && userData.value.nickName)
    );

    // JWT Token 是否存在
    const jwtTokenExist = computed<boolean>(() => Boolean(token.value));

    // B2B 站來源攜帶 Token (fdtk) 參數
    const fdtk = computed<string>(() => tokenPayload.value.fdtk || '');

    const isDisplaySubscribe = computed<boolean>(() => {
      const registerAfter = dayjs(userData.value.startDate).add(60, 'day');

      if (registerAfter.diff(new Date().setHours(0, 0, 0, 0), 'day') > 0) {
        return false;
      }

      if (userData.value.pointDate) {
        const pDate = dayjs(userData.value.pointDate);

        if (pDate.diff(new Date().setHours(0, 0, 0, 0), 'day') > 0) {
          return false;
        }
      }

      if (userData.value.reserveDemoDate) {
        const startDate = new Date(userData.value.reserveDemoDate * 1000);

        const endDate = dayjs(startDate).add(90, 'day');
        if (dayjs(new Date()).isBetween(startDate, endDate, 'day')) {
          return false;
        }
      }

      return true;
    });

    /**
     * 重置登入狀態
     */
    const resetLoginState = async (): Promise<void> => {
      isLogin.value = false;
      isFirstLogin.value = false;
    };

    /**
     * 初始化或重置使用者資料
     */
    const initUserData = async (): Promise<void> => {
      try {
        initialMemberInfo.memberId = 0;
        userData.value = { ...initialMemberInfo };
        needGetMemberInfo.value = true;
        token.value = '';
        initialMemberInfo.customerId = 411;
        initialMemberInfo.chatIds.aiTeacherAssistant = '';
        phoneExist.value = null;
        isWithdrawCoupon.value = null;
        hasSales.value = null;
        isPayUser.value = null;
      } catch (errors: unknown) {
        FDMessage.error(`初始化或重置使用者資料失敗。`);
        throw new Error(`[User Store] 初始化或重置使用者資料失敗: ${JSON.stringify(errors)}`);
      }
    };

    /**
     * 設置登入狀態
     * @param newToken - JWT Token
     * @param firstLogin - 是否為第一次登入
     */
    const setLoginState = (newToken: string, firstLogin: boolean = true): void => {
      isLogin.value = true;
      token.value = newToken;
      isFirstLogin.value = firstLogin;
    };

    /**
     * 清除使用者登入相關的狀態資訊
     */
    const clearUsersLoginResults = async (): Promise<void> => {
      await resetLoginState();

      if (rememberAccountAndPassword.value) {
        await initUserData();
      } else {
        UtilCommon.removeLocalStorage(LocalStorageKeys.UserStore);
      }

      // 清除 AI 助教、系統環境、功能造訪紀錄狀態資訊
      UtilCommon.removeLocalStorage(LocalStorageKeys.AiChatStore);
      window.sessionStorage.removeItem(LocalStorageKeys.AppStore);
      UtilCommon.removeLocalStorage(LocalStorageKeys.Log);
    };

    // 是否正在執行登出流程標記
    const isLoggingOut = ref<boolean>(false);

    /**
     * 決定登出後導向頁面
     *
     * @remarks customerId 為識別 B 端及 C 端會員的依據
     * - 指定 redirectUrl: 導向指定路徑
     * - 411 / 無 customerId: C 端會員，導向一般登入頁
     * - 其他: B 端會員，導向 B2B 官網登出頁
     */
    const goToLogoutHref = (isJunior: boolean, redirectUrl?: string): void => {
      if (redirectUrl) {
        window.location.href = redirectUrl;
        return;
      }

      const loginPage: string = isJunior ? '/loginJunior' : '/login';

      window.location.href =
        !customerId.value || customerId.value === 411
          ? loginPage
          : `${env.b2bOfficialUrl}/member/logout.asp`;
    };

    /**
     * 執行登出
     * @param redirectUrl - 登出後自訂導向路徑 (若無則依照會員類型導向預設頁)
     */
    const logout = async (redirectUrl?: string): Promise<void> => {
      isLoggingOut.value = true;

      // 在清除狀態前先判斷國高中生身份，避免 resetLoginState 後資料已清除
      const isJunior: boolean = isLogin.value && userData.value?.roleType === RoleTypeEnum.isJunior;

      try {
        // 發送登出請求給後端
        const memberId: number | undefined = userData.value?.memberId;
        if (memberId) await userApi.logout(memberId);

        // 活動日誌 - 記錄登出
        void createActivityLogsEvt({
          eventTypeId: ACTIVITY_LOG_EVENTS.LogOut.eventTypeId,
          details: t(ACTIVITY_LOG_EVENTS.LogOut.description, {
            nickname: userNickName
          })
        });

        // 清除 root domain cookie token
        removeToken();
        setLoginStateCookie(false);
      } catch (errors: unknown) {
        console.error(`[User Store] 登出請求發生異常: ${JSON.stringify(errors)}`);
      }

      // 清除前端登入狀態並導向相應頁面 (不論登出請求成功與否)
      goToLogoutHref(isJunior, redirectUrl);
      clearUsersLoginResults();
    };

    return {
      userData,
      social_Notification,
      functionAccess,
      userAccount,
      userPassword,
      rememberAccountAndPassword,
      token,
      isLogin,
      isFirstLogin,
      needGetMemberInfo,
      adId,
      adImageSrc,
      adRole,
      isAfterSignUp,
      fdtk,
      popupFlag,
      forceLogoutMsg,
      unAuthorized,
      earlyReserveOriginalCount,
      hasSales,
      isB2bUser,
      isPayUser,
      isPlatformExpired,
      isPayUserActive,
      phoneExist,
      isWithdrawCoupon,
      remainingTickets,
      customerId,
      userNickName,
      userAvatar,
      levelLabel,
      aiTeacherAssistant,
      isDisplaySubscribe,
      isAfterThirdPartVerify,
      courseFilterType,
      showCoffeeCouponByStore,
      hasUserInfo,
      jwtTokenExist,
      ayceContractInfo,
      hasLoginCondition,
      userSiteVersion,
      isV2OfficialUser,
      hiddenSubscriptionBtn,
      resetLoginState,
      initUserData,
      setLoginState,
      goToLogoutHref,
      logout,
      isLoggingOut
    };
  },
  {
    persist: {
      key: LocalStorageKeys.UserStore,
      storage: localStorage,
      paths: [
        'userAccount',
        'userPassword',
        'rememberAccountAndPassword',
        'isLogin',
        'adRole',
        'userData.memberId',
        'userData.hasLevel',
        'userData.themeSelection',
        'customerId',
        'aiTeacherAssistant',
        'phoneExist',
        'isWithdrawCoupon',
        'hasSales',
        'isPayUser',
        'courseFilterType',
        'showCoffeeCouponByStore'
      ]
    }
  }
);
